import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, Award, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export const BankComparison = ({ onApply }) => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(500000);
  const [tenure, setTenure] = useState(36);
  const [creditScore, setCreditScore] = useState("700");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("interest");

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const cs = Number(creditScore);
    let list = banks.filter(b => b.max_loan_amount >= amount && b.max_tenure_months >= tenure && b.min_credit_score <= cs);
    if (typeFilter !== "all") list = list.filter(b => b.type === typeFilter);
    if (sortBy === "interest") list = [...list].sort((a, b) => a.interest_rate_min - b.interest_rate_min);
    if (sortBy === "fee") list = [...list].sort((a, b) => a.processing_fee_min - b.processing_fee_min);
    if (sortBy === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [banks, amount, tenure, creditScore, typeFilter, sortBy]);

  const calcEmi = (P, annualRate, months) => {
    const r = annualRate / 12 / 100;
    return (P * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
  };

  return (
    <section id="compare" className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Live Comparison</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium text-slate-900">
            Every major Indian lender. One honest table.
          </h2>
          <p className="mt-3 text-slate-600 text-base leading-relaxed">
            Set your requirement below. We'll instantly filter lenders you're eligible for and rank by cost.
          </p>
        </div>

        {/* Filters */}
        <div className="mt-10 bg-slate-50 border border-slate-200 rounded-2xl p-6 lg:p-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Loan Amount</label>
              <span className="text-sm font-semibold text-slate-900" data-testid="filter-amount-value">{formatINR(amount)}</span>
            </div>
            <Slider data-testid="filter-amount" min={50000} max={5000000} step={50000} value={[amount]} onValueChange={(v) => setAmount(v[0])} />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1.5">
              <span>₹50K</span><span>₹50L</span>
            </div>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Tenure</label>
              <span className="text-sm font-semibold text-slate-900" data-testid="filter-tenure-value">{tenure} months</span>
            </div>
            <Slider data-testid="filter-tenure" min={6} max={96} step={6} value={[tenure]} onValueChange={(v) => setTenure(v[0])} />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1.5">
              <span>6 mo</span><span>96 mo</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 block mb-2">Credit Score</label>
            <Select value={creditScore} onValueChange={setCreditScore}>
              <SelectTrigger data-testid="filter-credit-score" className="bg-white border-slate-300 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="800">Excellent (800+)</SelectItem>
                <SelectItem value="750">Very Good (750+)</SelectItem>
                <SelectItem value="700">Good (700+)</SelectItem>
                <SelectItem value="650">Fair (650+)</SelectItem>
                <SelectItem value="600">Below Average</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 block mb-2">Lender Type</label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="filter-type" className="bg-white border-slate-300 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lenders</SelectItem>
                <SelectItem value="Bank">Banks Only</SelectItem>
                <SelectItem value="NBFC">NBFCs Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Sort */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            Showing <span className="font-semibold text-slate-900" data-testid="results-count">{filtered.length}</span> lenders matching your criteria
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Sort by</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-44 bg-white border-slate-300 h-9 text-sm" data-testid="filter-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="interest">Lowest Interest</SelectItem>
                <SelectItem value="fee">Lowest Fee</SelectItem>
                <SelectItem value="rating">Top Rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">
          Rates and fees shown are indicative, sourced from public lender information, and may not reflect
          the latest changes. Loan81 is not a lender — final rate, eligibility and approval are decided
          solely by the bank/NBFC.
        </p>

        {/* Results grid */}
        <div className="mt-6 grid gap-3">
          {loading && [...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 border border-dashed border-slate-300 rounded-2xl">
              <div className="text-slate-700 font-semibold">No lenders match your filters</div>
              <div className="text-sm text-slate-500 mt-1">Try lowering the credit score requirement or adjusting the amount.</div>
            </div>
          )}
          {!loading && filtered.map((b, idx) => {
            const emi = calcEmi(amount, b.interest_rate_min, tenure);
            const totalPayable = emi * tenure;
            const isBest = idx === 0 && sortBy === "interest";
            return (
              <div
                key={b.id}
                data-testid={`bank-row-${b.id}`}
                className={`stagger-item card-hover bg-white border rounded-2xl p-5 lg:p-6 grid lg:grid-cols-12 gap-4 items-center ${isBest ? "border-emerald-500 bg-emerald-50/40" : "border-slate-200"}`}
                style={{ animationDelay: `${idx * 40}ms` }}
              >
                <div className="lg:col-span-3 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 font-display font-bold text-white text-lg" style={{ backgroundColor: b.logo_color }}>
                    {b.short_name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-slate-900 truncate">{b.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="secondary" className="text-[10px] font-semibold h-5 px-1.5 bg-slate-100">{b.type}</Badge>
                      <span className="flex items-center gap-0.5 text-xs text-slate-500">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {b.rating.toFixed(1)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Interest</div>
                  <div className="font-display font-semibold text-lg text-slate-900">{b.interest_rate_min}%</div>
                  <div className="text-xs text-slate-500">up to {b.interest_rate_max}%</div>
                </div>

                <div className="lg:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Processing Fee</div>
                  <div className="font-display font-semibold text-lg text-slate-900">{b.processing_fee_min}%</div>
                  <div className="text-xs text-slate-500">up to {b.processing_fee_max}%</div>
                </div>

                <div className="lg:col-span-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Est. EMI</div>
                  <div className="font-display font-semibold text-lg text-slate-900" data-testid={`emi-${b.id}`}>
                    ₹{Math.round(emi).toLocaleString("en-IN")}
                  </div>
                  <div className="text-xs text-slate-500">Total: {formatINR(totalPayable)}</div>
                </div>

                <div className="lg:col-span-3 flex flex-col gap-2 lg:items-end">
                  {isBest && (
                    <div className="flex items-center gap-1 text-xs font-bold text-emerald-700">
                      <Award className="w-3.5 h-3.5" /> BEST MATCH
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row lg:flex-col gap-2 w-full lg:w-auto">
                    <Button
                      data-testid={`apply-${b.id}`}
                      onClick={() => onApply?.(b)}
                      className="bg-slate-900 hover:bg-slate-800 text-white w-full lg:w-auto rounded-md active:scale-95 transition-all"
                    >
                      Apply Now <ArrowUpRight className="ml-1 w-4 h-4" />
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      data-testid={`details-${b.id}`}
                      className="border-slate-300 text-slate-700 hover:bg-slate-50 w-full lg:w-auto"
                    >
                      <Link to={`/loans/${b.slug}`}>View details</Link>
                    </Button>
                  </div>
                </div>

                <div className="lg:col-span-12 flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
                  {b.features.slice(0, 4).map((f) => (
                    <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{f}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
